import { motion, type MotionValue } from "motion/react";
import { FC, MouseEvent, ReactNode, useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";

import {
  KeyboardKeys,
  rideInvestigationRideIdAtom,
  useSystemParams,
  type TDrawerCloseFunction,
} from "@ryde/shared";

import { DrawerBackdropStyled } from "./backdrop.styled";

interface IDrawerContainer {
  onCloseDrawer: TDrawerCloseFunction;
  children: ReactNode;
  drawerWidth: string | MotionValue<string>;
}

export const DrawerBackdrop: FC<IDrawerContainer> = ({
  onCloseDrawer,
  children,
  drawerWidth,
}) => {
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const isMouseDownWasInsideDrawer = useRef(false);
  const { disableFormCloseOnBackdropClick } = useSystemParams();

  const onMouseDownDrawerContent = (): void => {
    isMouseDownWasInsideDrawer.current = true;
  };

  const onMouseUp = (event: MouseEvent): void => {
    if (
      event.target === backdropRef.current &&
      !isMouseDownWasInsideDrawer.current &&
      !disableFormCloseOnBackdropClick
    ) {
      onCloseDrawer();
    }

    isMouseDownWasInsideDrawer.current = false;
  };

  const isInvestigationOpen =
    useRecoilValue(rideInvestigationRideIdAtom) !== undefined;

  useEffect(() => {
    const closeOnEscapePressed = (e: KeyboardEvent): void => {
      if (e.key === KeyboardKeys.ESCAPE) {
        onCloseDrawer();
      }
    };

    const preventEnterOutsideDrawer = (e: KeyboardEvent): void => {
      // The investigation overlay is a DOM sibling of the drawer, so every ENTER inside it counts as
      // "outside" and would be swallowed. While it is open the drawer is not the active surface.
      if (isInvestigationOpen) {
        return;
      }

      if (e.key === KeyboardKeys.ENTER) {
        const activeEl = document.activeElement;
        if (
          backdropRef.current &&
          activeEl &&
          !backdropRef.current.contains(activeEl)
        ) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    window.addEventListener("keydown", closeOnEscapePressed);
    window.addEventListener("keydown", preventEnterOutsideDrawer, true);
    return () => {
      window.removeEventListener("keydown", closeOnEscapePressed);
      window.removeEventListener("keydown", preventEnterOutsideDrawer, true);
    };
  }, [onCloseDrawer, isInvestigationOpen]);

  return (
    <DrawerBackdropStyled ref={backdropRef} onMouseUp={onMouseUp}>
      <motion.div
        style={{ width: drawerWidth }}
        onMouseDown={onMouseDownDrawerContent}
      >
        {children}
      </motion.div>
    </DrawerBackdropStyled>
  );
};
